/* eslint-disable max-lines */
import { NetworkStatusDisplayTextMap, NetworkStatuses } from '@strata/constants';
import { conditionalString, DateFormats, formatDate, sortAsc } from '@strata/helper-functions';
import { SingleSelectWithOther } from '@strata/react-compound-inputs';
import { PhoneInput } from '@strata/react-inputs';
import { validationStrings } from '@strata/strata-ui-strings';
import dayjs from 'dayjs';
import * as Yup from 'yup';

import {
    ORG_SIZE_OPTIONS,
    PHARMACY_TYPE_OPTIONS,
    SWITCH_PROVIDER_OPTIONS,
} from 'services/constants';
import { emptyOnNull, nullOnEmpty } from 'services/Helpers';

export const EMPTY_ADDITIONAL_OPS_CONTACT = {
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
};

/** Map supplier reference data to select options, sorted by display name. */
export const mapSuppliersToOptions = (suppliers) =>
    suppliers
        ?.map((supplier) => ({
            ...supplier,
            name: `${supplier.organization_name}${conditionalString(supplier.network_status !== NetworkStatuses.ACTIVE, ` (${NetworkStatusDisplayTextMap[supplier.network_status]})`)}`,
            value: supplier.supplier_npi,
            secondaryData: supplier.supplier_npi,
        }))
        .sort((a, b) => sortAsc(a.name, b.name));

const boolToYesNo = (value) => (value ? 'Yes' : 'No');
const yesNoToBool = (value) => value === 'Yes';

const commonInitialValues = {
    npi: '',
    organization_name: '',
    first_name: '',
    last_name: '',
    telephone_number: '',
    fax_number: '',
    address_1: '',
    address_2: '',
    city: '',
    state: '',
    postal_code: '',
};

export const supplierInitialValues = {
    ...commonInitialValues,
    tax_id: '',
    payto_address_1: '',
    payto_address_2: '',
    payto_city: '',
    payto_state: '',
    payto_postal_code: '',
    network_status: '',
    email: '',
    business_hours: '',
    pharmacy_website: '',
    businessops_contact_name: '',
    businessops_contact_email: '',
    businessops_contact_phone: '',
    contracting_contact_name: '',
    contracting_contact_email: '',
    contracting_contact_phone: '',
    primary_contact_email: '',
    primary_contact_phone: '',
    secondary_contact_first_name: '',
    secondary_contact_last_name: '',
    secondary_contact_email: '',
    secondary_contact_phone: '',
    legal_notice_email: '',
    breach_notice_email: '',
    pharmacy_type: undefined,
    psao: 'None',
    psaoOther: undefined,
    accreditations: [],
    additional_ops_contacts: [],
};

export const prescriberInitialValues = {
    ...commonInitialValues,
    practice_npi: '',
    taxonomy_code: '',
    classification: '',
    specialization: '',
    last_update: formatDate(dayjs(), DateFormats.HYPHEN_DATE),
};

const loadCommonValues = (values) => ({
    organization_name: emptyOnNull(values?.organization_name),
    first_name: emptyOnNull(values?.first_name),
    last_name: emptyOnNull(values?.last_name),
    telephone_number: emptyOnNull(values?.telephone_number),
    fax_number: emptyOnNull(values?.fax_number),
    address_1: emptyOnNull(values?.address_1),
    address_2: emptyOnNull(values?.address_2),
    city: emptyOnNull(values?.city),
    state: emptyOnNull(values?.state),
    postal_code: emptyOnNull(values?.postal_code),
    taxonomy_code: emptyOnNull(values?.taxonomy_code),
    classification: emptyOnNull(values?.classification),
    specialization: emptyOnNull(values?.specialization),
});

// eslint-disable-next-line complexity
export const loadSupplierValues = (values, taxonomyOptions) => ({
    npi: values?.supplier_npi,
    billing_supplier_npi: values?.billing_supplier_npi,
    ...loadCommonValues(values),
    tax_id: emptyOnNull(values?.tax_id),
    payto_address_1: emptyOnNull(values?.payto_address_1),
    payto_address_2: emptyOnNull(values?.payto_address_2),
    payto_city: emptyOnNull(values?.payto_city),
    payto_state: emptyOnNull(values?.payto_state),
    payto_postal_code: emptyOnNull(values?.payto_postal_code),
    network_status: emptyOnNull(values?.network_status),
    email: emptyOnNull(values?.email),
    business_hours: emptyOnNull(values?.business_hours),
    pharmacy_website: emptyOnNull(values?.pharmacy_website),
    businessops_contact_name: emptyOnNull(values?.businessops_contact_name),
    businessops_contact_email: emptyOnNull(values?.businessops_contact_email),
    businessops_contact_phone: emptyOnNull(values?.businessops_contact_phone),
    contracting_contact_name: emptyOnNull(values?.contracting_contact_name),
    contracting_contact_email: emptyOnNull(values?.contracting_contact_email),
    contracting_contact_phone: emptyOnNull(values?.contracting_contact_phone),
    primary_contact_email: emptyOnNull(values?.primary_contact_email),
    primary_contact_phone: emptyOnNull(values?.primary_contact_phone),
    secondary_contact_first_name: emptyOnNull(values?.secondary_contact_first_name),
    secondary_contact_last_name: emptyOnNull(values?.secondary_contact_last_name),
    secondary_contact_email: emptyOnNull(values?.secondary_contact_email),
    secondary_contact_phone: emptyOnNull(values?.secondary_contact_phone),
    legal_notice_email: emptyOnNull(values?.legal_notice_email),
    breach_notice_email: emptyOnNull(values?.breach_notice_email),
    ncpdp_id: emptyOnNull(values?.ncpdp_id),
    send_do_not_fill: boolToYesNo(values?.send_do_not_fill),
    does_own_paa: boolToYesNo(values?.does_own_paa),
    org_size: emptyOnNull(values?.org_size),
    switch_provider: emptyOnNull(values?.switch_provider),
    pharmacy_software: emptyOnNull(values?.pharmacy_software),
    taxonomies: taxonomyOptions?.filter((option) =>
        values?.extra_taxonomy_codes?.find(
            (taxonomy) => option.taxonomy_code === taxonomy.taxonomy_code,
        ),
    ),
    also_send_fax: boolToYesNo(values?.also_send_fax),
    pharmacy_type: values?.pharmacy_type || undefined,
    psao: values?.psao,
    accreditations: values?.accreditations?.length ? values.accreditations : [],
    additional_ops_contacts: (values?.additional_ops_contacts || []).map((contact) => ({
        supplier_contact_id: contact.supplier_contact_id,
        first_name: emptyOnNull(contact.first_name),
        last_name: emptyOnNull(contact.last_name),
        email: emptyOnNull(contact.email),
        phone: emptyOnNull(contact.phone),
    })),
});

export const loadOrtfValues = (ortfConfig) => ({
    ortf_outbound_enabled: ortfConfig?.ortf_outbound_enabled ? 'Yes' : 'No',
    receiving_pharmacist_first_name: emptyOnNull(ortfConfig?.receiving_pharmacist_first_name),
    receiving_pharmacist_last_name: emptyOnNull(ortfConfig?.receiving_pharmacist_last_name),
    receiving_pharmacist_emails: ortfConfig?.receiving_pharmacist_emails?.length
        ? ortfConfig.receiving_pharmacist_emails
        : [''],
    segment_by_payer: ortfConfig?.segment_by_payer ? 'Yes' : 'No',
    segment_by_bin_pcn: ortfConfig?.segment_by_bin_pcn ? 'Yes' : 'No',
    script_count_threshold: ortfConfig?.script_count_threshold ?? 25,
});

export const packageOrtfConfig = (values, existingOrtfConfig) => ({
    pharmacy_npi: values.npi,
    ortf_outbound_enabled: values.ortf_outbound_enabled === 'Yes',
    receiving_pharmacist_first_name: values.receiving_pharmacist_first_name,
    receiving_pharmacist_last_name: values.receiving_pharmacist_last_name,
    receiving_pharmacist_emails: values.receiving_pharmacist_emails.filter(Boolean),
    receiving_npi: existingOrtfConfig?.receiving_npi ?? values.npi,
    segment_by_payer: values.segment_by_payer === 'Yes',
    segment_by_bin_pcn: values.segment_by_bin_pcn === 'Yes',
    script_count_threshold: Number(values.script_count_threshold),
});

/** Prescriber referral assignment communication preference */
export const ReferralAssignmentCommPreference = {
    /** Receive standard prescriber fax */
    FAX: 'FAX',
    /** Do not contact the prescriber */
    NO_CONTACT: 'NO_CONTACT',
};

export const loadPrescriberValues = (values) => ({
    npi: values?.prescriber_npi,
    ...loadCommonValues(values),
    practice_npi: emptyOnNull(values?.practice_npi),
    referral_assignment_comm_preference:
        values?.referral_assignment_comm_preference ?? ReferralAssignmentCommPreference.FAX,
    last_update: emptyOnNull(values?.last_update),
});

const baseSchema = {
    npi: Yup.string()
        .required(validationStrings.requiredField.errorMessage)
        .length(10, 'Must be exactly 10 digits'),
    first_name: Yup.string()
        .max(50, validationStrings.maxCharacter[50])
        .required(validationStrings.requiredField.errorMessage),
    last_name: Yup.string()
        .max(50, validationStrings.maxCharacter[50])
        .required(validationStrings.requiredField.errorMessage),
    telephone_number: PhoneInput.validator().required(validationStrings.requiredField.errorMessage),
    fax_number: PhoneInput.validator(),
    address_1: Yup.string()
        .max(100, validationStrings.maxCharacter[100])
        .required(validationStrings.requiredField.errorMessage),
    address_2: Yup.string().max(100, validationStrings.maxCharacter[100]),
    city: Yup.string()
        .max(50, validationStrings.maxCharacter[50])
        .required(validationStrings.requiredField.errorMessage),
    state: Yup.string()
        .required(validationStrings.requiredField.errorMessage)
        .length(2, 'Must be exactly 2 characters'),
    postal_code: Yup.string()
        .min(5, 'Must be at least 5 characters')
        .max(10, validationStrings.maxCharacter.lambda(10))
        .required(validationStrings.requiredField.errorMessage),
};

export const supplierSchema = Yup.object({
    ...baseSchema,
    organization_name: Yup.string()
        .max(100, validationStrings.maxCharacter[100])
        .required(validationStrings.requiredField.errorMessage),
    tax_id: Yup.string()
        .max(20, validationStrings.maxCharacter.lambda(20))
        .required(validationStrings.requiredField.errorMessage),
    payto_address_1: Yup.string()
        .max(100, validationStrings.maxCharacter[100])
        .required(validationStrings.requiredField.errorMessage),
    payto_address_2: Yup.string().max(100, validationStrings.maxCharacter[100]),
    payto_city: Yup.string()
        .max(50, validationStrings.maxCharacter[50])
        .required(validationStrings.requiredField.errorMessage),
    payto_state: Yup.string()
        .required(validationStrings.requiredField.errorMessage)
        .length(2, 'Must be exactly 2 characters'),
    payto_postal_code: Yup.string()
        .min(5, 'Must be at least 5 characters')
        .max(10, validationStrings.maxCharacter.lambda(10))
        .required(validationStrings.requiredField.errorMessage),
    network_status: Yup.string().required(validationStrings.requiredField.errorMessage),
    billing_supplier_npi: Yup.string()
        .required(validationStrings.requiredField.errorMessage)
        .length(10, 'Must be exactly 10 characters'),
    org_size: Yup.string()
        .oneOf(ORG_SIZE_OPTIONS)
        .required(validationStrings.requiredField.errorMessage),
    switch_provider: Yup.string().oneOf(SWITCH_PROVIDER_OPTIONS),
    pharmacy_software: Yup.string().max(100, validationStrings.maxCharacter[100]),
    primary_contact_email: Yup.string().max(200, validationStrings.maxCharacter.lambda(200)),
    primary_contact_phone: Yup.string().max(100, validationStrings.maxCharacter[100]),
    secondary_contact_first_name: Yup.string().max(50, validationStrings.maxCharacter[50]),
    secondary_contact_last_name: Yup.string().max(50, validationStrings.maxCharacter[50]),
    secondary_contact_email: Yup.string().max(200, validationStrings.maxCharacter.lambda(200)),
    secondary_contact_phone: Yup.string().max(100, validationStrings.maxCharacter[100]),
    business_hours: Yup.string().max(400, validationStrings.maxCharacter.lambda(400)),
    pharmacy_website: Yup.string().max(200, validationStrings.maxCharacter.lambda(200)),
    businessops_contact_name: Yup.string().max(200, validationStrings.maxCharacter.lambda(200)),
    businessops_contact_email: Yup.string().max(200, validationStrings.maxCharacter.lambda(200)),
    businessops_contact_phone: Yup.string().max(100, validationStrings.maxCharacter[100]),
    contracting_contact_name: Yup.string().max(200, validationStrings.maxCharacter.lambda(200)),
    contracting_contact_email: Yup.string().max(200, validationStrings.maxCharacter.lambda(200)),
    contracting_contact_phone: Yup.string().max(100, validationStrings.maxCharacter[100]),
    legal_notice_email: Yup.string().max(200, validationStrings.maxCharacter.lambda(200)),
    breach_notice_email: Yup.string().max(200, validationStrings.maxCharacter.lambda(200)),
    pharmacy_type: Yup.string().oneOf(PHARMACY_TYPE_OPTIONS),
    ...SingleSelectWithOther.validationSchema({ id: 'psao' }),
    psao: Yup.string().nullable(),
    accreditations: Yup.array().of(Yup.string().max(100, validationStrings.maxCharacter[100])),
    additional_ops_contacts: Yup.array().of(
        Yup.object({
            first_name: Yup.string().max(100, validationStrings.maxCharacter[100]),
            last_name: Yup.string().max(100, validationStrings.maxCharacter[100]),
            email: Yup.string().max(100, validationStrings.maxCharacter[100]),
            phone: Yup.string().max(50, validationStrings.maxCharacter[50]),
        }),
    ),
    ortf_outbound_enabled: Yup.string().nullable().oneOf(['Yes', 'No']),
    receiving_pharmacist_first_name: Yup.string()
        .nullable()
        .max(50, validationStrings.maxCharacter[50]),
    receiving_pharmacist_last_name: Yup.string()
        .nullable()
        .max(50, validationStrings.maxCharacter[50]),
    receiving_pharmacist_emails: Yup.array().of(
        Yup.string().max(100, validationStrings.maxCharacter[100]),
    ),
    segment_by_payer: Yup.string().nullable().oneOf(['Yes', 'No']),
    segment_by_bin_pcn: Yup.string().nullable().oneOf(['Yes', 'No']),
    script_count_threshold: Yup.number()
        .nullable()
        .integer('Must be a whole number')
        .min(1, 'Must be at least 1'),
});

export const supplierChildSchema = Yup.object({
    ...baseSchema,
    organization_name: Yup.string()
        .max(100, validationStrings.maxCharacter[100])
        .required(validationStrings.requiredField.errorMessage),
    payto_address_1: Yup.string()
        .max(100, validationStrings.maxCharacter[100])
        .required(validationStrings.requiredField.errorMessage),
    payto_address_2: Yup.string().max(100, validationStrings.maxCharacter[100]),
    payto_city: Yup.string()
        .max(50, validationStrings.maxCharacter[50])
        .required(validationStrings.requiredField.errorMessage),
    payto_state: Yup.string()
        .required(validationStrings.requiredField.errorMessage)
        .length(2, 'Must be exactly 2 characters'),
    payto_postal_code: Yup.string()
        .min(5, 'Must be at least 5 characters')
        .max(10, validationStrings.maxCharacter.lambda(10))
        .required(validationStrings.requiredField.errorMessage),
    network_status: Yup.string().required(validationStrings.requiredField.errorMessage),
    taxonomy_code: Yup.string().length(10, 'Must be exactly 10 digits'),
    classification: Yup.string().max(100, validationStrings.maxCharacter[100]),
    specialization: Yup.string().max(100, validationStrings.maxCharacter[100]),
    ncpdp_id: Yup.string()
        .required(validationStrings.requiredField.errorMessage)
        .min(6, 'Must be at least 6 digits')
        .max(7, validationStrings.maxCharacter.lambda(7)),
    taxonomies: Yup.array(),
});

export const prescriberSchema = Yup.object({
    ...baseSchema,
    organization_name: Yup.string().max(100, validationStrings.maxCharacter[100]),
    practice_npi: Yup.string().length(10, 'Must be exactly 10 digits'),
    taxonomy_code: Yup.string()
        .required(validationStrings.requiredField.errorMessage)
        .length(10, 'Must be exactly 10 digits'),
    classification: Yup.string()
        .max(100, validationStrings.maxCharacter[100])
        .required(validationStrings.requiredField.errorMessage),
    specialization: Yup.string().max(100, validationStrings.maxCharacter[100]),
});

const getBaseFields = (values) => ({
    organization_name: values.organization_name,
    first_name: values.first_name,
    last_name: values.last_name,
    telephone_number: values.telephone_number,
    fax_number: values.fax_number,
    address_1: values.address_1,
    address_2: nullOnEmpty(values.address_2),
    city: values.city,
    state: values.state,
    postal_code: values.postal_code,
    taxonomy_code: values.taxonomy_code,
    classification: values.classification,
    specialization: nullOnEmpty(values.specialization),
});

const packageAdditionalOpsContacts = (values) =>
    (values.additional_ops_contacts || [])
        .filter(
            (contact) =>
                contact.first_name?.trim() ||
                contact.last_name?.trim() ||
                contact.email?.trim() ||
                contact.phone?.trim(),
        )
        .map((contact) => ({
            supplier_contact_id: contact.supplier_contact_id || null,
            first_name: nullOnEmpty(contact.first_name),
            last_name: nullOnEmpty(contact.last_name),
            email: nullOnEmpty(contact.email),
            phone: nullOnEmpty(contact.phone),
        }));

export const packageSupplier = (values) => ({
    supplier_npi: values.npi,
    billing_supplier_npi: values.billing_supplier_npi,
    ...getBaseFields(values),
    tax_id: values.tax_id,
    payto_address_1: values.payto_address_1,
    payto_address_2: nullOnEmpty(values.payto_address_2),
    payto_city: values.payto_city,
    payto_state: values.payto_state,
    payto_postal_code: values.payto_postal_code,
    network_status: nullOnEmpty(values.network_status),
    email: values.email,
    business_hours: nullOnEmpty(values.business_hours),
    pharmacy_website: nullOnEmpty(values.pharmacy_website),
    businessops_contact_name: nullOnEmpty(values.businessops_contact_name),
    businessops_contact_email: nullOnEmpty(values.businessops_contact_email),
    businessops_contact_phone: nullOnEmpty(values.businessops_contact_phone),
    contracting_contact_name: nullOnEmpty(values.contracting_contact_name),
    contracting_contact_email: nullOnEmpty(values.contracting_contact_email),
    contracting_contact_phone: nullOnEmpty(values.contracting_contact_phone),
    primary_contact_email: nullOnEmpty(values.primary_contact_email),
    primary_contact_phone: nullOnEmpty(values.primary_contact_phone),
    secondary_contact_first_name: nullOnEmpty(values.secondary_contact_first_name),
    secondary_contact_last_name: nullOnEmpty(values.secondary_contact_last_name),
    secondary_contact_email: nullOnEmpty(values.secondary_contact_email),
    secondary_contact_phone: nullOnEmpty(values.secondary_contact_phone),
    legal_notice_email: nullOnEmpty(values.legal_notice_email),
    breach_notice_email: nullOnEmpty(values.breach_notice_email),
    ncpdp_id: values.ncpdp_id,
    send_do_not_fill: yesNoToBool(values.send_do_not_fill),
    does_own_paa: yesNoToBool(values.does_own_paa),
    org_size: nullOnEmpty(values.org_size),
    switch_provider: nullOnEmpty(values.switch_provider),
    pharmacy_software: nullOnEmpty(values.pharmacy_software),
    extra_taxonomy_codes: values.taxonomies || [],
    also_send_fax: yesNoToBool(values.also_send_fax),
    pharmacy_type: nullOnEmpty(values.pharmacy_type),
    psao: nullOnEmpty(SingleSelectWithOther.submitValues({ id: 'psao' })(values).psao),
    accreditations: values.accreditations?.length ? values.accreditations : null,
    additional_ops_contacts: packageAdditionalOpsContacts(values),
});

export const packagePrescriber = (values) => ({
    prescriber_npi: values.npi,
    ...getBaseFields(values),
    practice_npi: values.practice_npi,
    referral_assignment_comm_preference: values.referral_assignment_comm_preference,
    last_update: formatDate(dayjs(), DateFormats.HYPHEN_DATE),
    override_orderly: true,
});
